"use client"

import { addToCart } from "@lib/data/cart"
import {
  ProductBySku,
  SystemBuilderData,
  SystemBuilderShroud,
} from "@lib/data/products"
import { convertToLocale } from "@lib/util/money"
import FinancingMessage from "@modules/common/components/financing-message"
import LocalizedClientLink from "@modules/common/components/localized-client-link"
import Modal from "@modules/common/components/modal"
import { Heading, Text } from "@modules/common/components/ui"
import X from "@modules/common/icons/x"
import { useParams } from "next/navigation"
import { Fragment, useEffect, useMemo, useRef, useState } from "react"

import {
  AddedCheck,
  AmberButton,
  ChoiceCard,
  DeclineLink,
  ProgressDots,
  Thumb,
} from "./ui"

type StepId = "path" | "shroud" | "fans" | "relay" | "extras" | "summary"

const DOT_STEPS: StepId[] = ["path", "shroud", "fans", "relay", "extras"]

type AddedItem = {
  variant_id: string
  title: string
  thumbnail: string | null
  quantity: number
  price: number | null
  currency_code: string | null
}

type SystemWizardProps = {
  isOpen: boolean
  close: () => void
  // System builder data for the finish of the variant the shopper added.
  data: SystemBuilderData
}

const fmtPrice = (price: number | null, currencyCode: string | null) =>
  price != null && currencyCode
    ? convertToLocale({ amount: price, currency_code: currencyCode })
    : null

const filterColorway = (sku: string) =>
  sku.includes("-CHR") ? "Chrome" : sku.includes("-BLK") ? "Black" : "Grey"

// "OF 2 x 10-BLK" -> 2" x 10"
const tankSize = (sku: string) => {
  const match = sku.match(/(\d+)\s*x\s*(\d+)/)
  return match ? `${match[1]}" x ${match[2]}"` : null
}

// Guided post-add-to-cart wizard for radiators: shroud, fans, relay, then
// filter + overflow tank — one quick decision per screen.
export default function SystemWizard({ isOpen, close, data }: SystemWizardProps) {
  const countryCode = useParams().countryCode as string

  const [step, setStep] = useState<StepId>("path")
  const [history, setHistory] = useState<StepId[]>([])
  const [chosenShroud, setChosenShroud] = useState<SystemBuilderShroud | null>(
    null
  )
  const [added, setAdded] = useState<AddedItem[]>([])
  const [fanQtyAdded, setFanQtyAdded] = useState(0)
  const [isAdding, setIsAdding] = useState(false)

  const addedIds = useMemo(
    () => new Set(added.map((i) => i.variant_id)),
    [added]
  )

  const shrouds = useMemo(() => data.shrouds.filter((s) => s.in_stock), [data])
  const fanConfigs = useMemo(
    () => data.fans_only.filter((c) => c.fans.some((f) => f.in_stock)),
    [data]
  )
  const filters = useMemo(() => data.filters.filter((f) => f.in_stock), [data])
  const tanks = useMemo(() => data.tanks.filter((t) => t.in_stock), [data])

  const goTo = (next: StepId) => {
    setHistory((prev) => [...prev, step])
    setStep(next)
  }

  const goBack = () => {
    if (!history.length) {
      return
    }
    setStep(history[history.length - 1])
    setHistory((prev) => prev.slice(0, -1))
  }

  const addItem = async (
    item: ProductBySku,
    quantity: number
  ): Promise<boolean> => {
    setIsAdding(true)
    try {
      await addToCart({ variantId: item.variant_id, quantity, countryCode })
      setAdded((prev) => [
        ...prev,
        {
          variant_id: item.variant_id,
          title: item.title,
          thumbnail: item.thumbnail,
          quantity,
          price: item.price,
          currency_code: item.currency_code,
        },
      ])
      return true
    } catch {
      // Leave the step as-is so the shopper can retry.
      return false
    } finally {
      setIsAdding(false)
    }
  }

  const relayPairFor = (fanQty: number) =>
    fanQty > 1 ? data.relays.dual : data.relays.single

  const relayAvailable = (fanQty: number) => {
    const pair = relayPairFor(fanQty)
    return Boolean(pair.standard?.in_stock || pair.bluetooth?.in_stock)
  }

  const extrasAvailable = filters.length > 0 || tanks.length > 0

  // End of the flow: show the summary if anything was added, otherwise just
  // close. `justAdded` covers navigation within the same handler as an add,
  // before the `added` state from that add is visible here.
  const finishFlow = (justAdded = false) => {
    if (justAdded || added.length) {
      goTo("summary")
    } else {
      close()
    }
  }

  const toExtras = (justAdded = false) => {
    if (extrasAvailable) {
      goTo("extras")
    } else {
      finishFlow(justAdded)
    }
  }

  const handleShroudAdd = async (shroud: SystemBuilderShroud) => {
    if (await addItem(shroud, 1)) {
      setChosenShroud(shroud)
      goTo("fans")
    }
  }

  const handleFanAdd = async (fan: ProductBySku, qty: number) => {
    if (await addItem(fan, qty)) {
      setFanQtyAdded(qty)
      if (relayAvailable(qty)) {
        goTo("relay")
      } else {
        toExtras(true)
      }
    }
  }

  const handleRelayAdd = async (relay: ProductBySku) => {
    if (await addItem(relay, 1)) {
      toExtras(true)
    }
  }

  const dotIndex = step === "summary" ? DOT_STEPS.length : DOT_STEPS.indexOf(step)

  return (
    <Modal
      isOpen={isOpen}
      close={close}
      size="large"
      aria-label="Complete your cooling system"
      data-testid="system-wizard"
    >
      <div className="flex items-center justify-between">
        <button
          type="button"
          onClick={goBack}
          aria-label="Back"
          data-testid="wizard-back-button"
          className={
            history.length && step !== "summary"
              ? "text-gray-500 hover:text-gray-800 p-1 -ml-1"
              : "invisible p-1 -ml-1"
          }
        >
          <svg width="20" height="20" viewBox="0 0 20 20" fill="none" aria-hidden="true">
            <path
              d="M12.5 4 6.5 10l6 6"
              stroke="currentColor"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </button>
        <ProgressDots total={DOT_STEPS.length} current={dotIndex} />
        <button
          type="button"
          onClick={close}
          aria-label="Close"
          data-testid="wizard-close-button"
          className="text-gray-500 hover:text-gray-800 p-1 -mr-1"
        >
          <X size={20} aria-hidden="true" />
        </button>
      </div>

      <div className="mt-3 overflow-y-auto">
        {step === "path" && (
          <PathStep
            shrouds={shrouds}
            fanConfigs={fanConfigs}
            onShroudPath={() => goTo("shroud")}
            onFansPath={() => goTo("fans")}
            onDecline={() => toExtras()}
          />
        )}
        {step === "shroud" && (
          <ShroudStep
            shrouds={shrouds}
            addedIds={addedIds}
            isAdding={isAdding}
            onAdd={handleShroudAdd}
            onDecline={() =>
              fanConfigs.length ? goTo("fans") : toExtras()
            }
          />
        )}
        {step === "fans" && (
          <FansStep
            shroud={chosenShroud}
            fanConfigs={fanConfigs}
            addedIds={addedIds}
            isAdding={isAdding}
            onAdd={handleFanAdd}
            onDecline={() => toExtras()}
          />
        )}
        {step === "relay" && (
          <RelayStep
            standard={relayPairFor(fanQtyAdded).standard}
            bluetooth={relayPairFor(fanQtyAdded).bluetooth}
            addedIds={addedIds}
            isAdding={isAdding}
            onAdd={handleRelayAdd}
            onDecline={() => toExtras()}
          />
        )}
        {step === "extras" && (
          <ExtrasStep
            filters={filters}
            tanks={tanks}
            inletDiameter={data.inlet_diameter}
            addedIds={addedIds}
            isAdding={isAdding}
            onAdd={(item) => addItem(item, 1)}
            onFinish={() => finishFlow()}
            onDecline={() => finishFlow()}
          />
        )}
        {step === "summary" && <SummaryStep added={added} close={close} />}
      </div>
    </Modal>
  )
}

function ItemRow({
  item,
  subtitle,
  added,
}: {
  item: ProductBySku
  subtitle?: string | null
  added?: boolean
}) {
  const price = fmtPrice(item.price, item.currency_code)

  return (
    <span className="flex items-center gap-x-3">
      <Thumb src={item.thumbnail} alt={item.title} className="w-16 h-16" />
      <span className="flex flex-col min-w-0 flex-1">
        <span className="text-sm font-medium text-gray-900 line-clamp-2">
          {item.title}
        </span>
        {subtitle && <span className="text-xs text-gray-500">{subtitle}</span>}
        {price && (
          <span className="text-sm font-semibold text-gray-900">{price}</span>
        )}
      </span>
      {added && <AddedCheck className="shrink-0" />}
    </span>
  )
}

function PathStep({
  shrouds,
  fanConfigs,
  onShroudPath,
  onFansPath,
  onDecline,
}: {
  shrouds: SystemBuilderShroud[]
  fanConfigs: SystemBuilderData["fans_only"]
  onShroudPath: () => void
  onFansPath: () => void
  onDecline: () => void
}) {
  const shroudThumb = shrouds[0]?.thumbnail ?? null
  const fanThumb =
    fanConfigs[0]?.fans.find((f) => f.in_stock)?.thumbnail ?? null

  // Pre-select the shroud path: deferred focus wins over the Headless UI
  // dialog focus trap when the modal first opens.
  const primaryRef = useRef<HTMLButtonElement>(null)
  useEffect(() => {
    const id = window.setTimeout(() => primaryRef.current?.focus(), 0)
    return () => window.clearTimeout(id)
  }, [])

  return (
    <div className="flex flex-col gap-y-4">
      <div>
        <Heading level="h2" className="text-xl">
          Keep it cool the right way
        </Heading>
        <Text className="text-sm text-gray-600 mt-1">
          Add a fan shroud with fans, or just fans?
        </Text>
      </div>

      {shrouds.length > 0 && (
        <button
          ref={primaryRef}
          type="button"
          onClick={onShroudPath}
          data-testid="wizard-path-shroud"
          className="w-full rounded-lg bg-amber-500 p-4 text-left flex items-center gap-x-4 text-white transition-colors hover:bg-amber-600 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-500 focus-visible:ring-offset-2"
        >
          <Thumb
            src={shroudThumb}
            alt="Fan shroud"
            className="w-20 h-20 bg-white"
          />
          <span className="flex flex-col min-w-0">
            <span className="text-base font-semibold">Shroud + Fans</span>
            <span className="text-sm font-normal text-amber-50">
              A shroud pulls air through the entire core instead of just where
              the fan sits — the single biggest airflow upgrade you can make.
            </span>
          </span>
        </button>
      )}

      {fanConfigs.length > 0 && (
        <button
          ref={shrouds.length ? undefined : primaryRef}
          type="button"
          onClick={onFansPath}
          data-testid="wizard-path-fans"
          className="w-full rounded-lg border border-gray-200 bg-white p-4 text-left flex items-center gap-x-4 hover:border-gray-300 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-500"
        >
          <Thumb src={fanThumb} alt="Electric fan" className="w-14 h-14" />
          <span className="flex flex-col">
            <span className="text-base font-semibold text-gray-900">
              Just Fans
            </span>
            <span className="text-sm text-gray-600">
              Bolt-on electric fans sized to your core.
            </span>
          </span>
        </button>
      )}

      <DeclineLink onClick={onDecline} data-testid="wizard-path-decline">
        Nah — my radiator likes living dangerously
      </DeclineLink>
    </div>
  )
}

function ShroudStep({
  shrouds,
  addedIds,
  isAdding,
  onAdd,
  onDecline,
}: {
  shrouds: SystemBuilderShroud[]
  addedIds: Set<string>
  isAdding: boolean
  onAdd: (shroud: SystemBuilderShroud) => void
  onDecline: () => void
}) {
  const [selectedId, setSelectedId] = useState(shrouds[0]?.variant_id ?? null)
  const selected = shrouds.find((s) => s.variant_id === selectedId) ?? null

  return (
    <div className="flex flex-col gap-y-4">
      <div>
        <Heading level="h2" className="text-xl">
          Pick your shroud
        </Heading>
        <Text className="text-sm text-gray-600 mt-1">
          Finish-matched to your radiator — no repainting, no rework.
        </Text>
      </div>

      <div className="flex flex-col gap-y-3">
        {shrouds.map((shroud) => {
          const isAddedItem = addedIds.has(shroud.variant_id)
          const isSelected = selectedId === shroud.variant_id

          return (
            <Fragment key={shroud.variant_id}>
              <ChoiceCard
                selected={isSelected}
                added={isAddedItem}
                onSelect={() => setSelectedId(shroud.variant_id)}
                data-testid="wizard-shroud-option"
              >
                <ItemRow
                  item={shroud}
                  subtitle={`Fits (${shroud.fan_qty}) ${shroud.fan_dia} inch fan${
                    shroud.fan_qty === 1 ? "" : "s"
                  }`}
                  added={isAddedItem}
                />
              </ChoiceCard>
              {isSelected && !isAddedItem && (
                <AmberButton
                  autoFocusOnMount
                  isLoading={isAdding}
                  onClick={() => onAdd(shroud)}
                  className="w-full"
                  data-testid="wizard-shroud-add"
                >
                  Add this shroud
                </AmberButton>
              )}
            </Fragment>
          )
        })}
      </div>

      <DeclineLink
        onClick={onDecline}
        disabled={isAdding}
        data-testid="wizard-shroud-decline"
      >
        Skip — bare radiator it is
      </DeclineLink>
    </div>
  )
}

function FansStep({
  shroud,
  fanConfigs,
  addedIds,
  isAdding,
  onAdd,
  onDecline,
}: {
  shroud: SystemBuilderShroud | null
  fanConfigs: SystemBuilderData["fans_only"]
  addedIds: Set<string>
  isAdding: boolean
  onAdd: (fan: ProductBySku, qty: number) => void
  onDecline: () => void
}) {
  // Fans-only path: best config first, shopper can switch.
  const [configIdx, setConfigIdx] = useState(0)

  const config = shroud ? null : fanConfigs[configIdx] ?? null
  const qty = shroud ? shroud.fan_qty : config?.qty ?? 1

  // Note: SPAL fan diameters intentionally differ from the shroud opening
  // (a 13" SPAL is correct for a 14" shroud) — never "fix" the sizes.
  const fans = useMemo(() => {
    const source = shroud ? shroud.fans : config?.fans ?? []
    return source.filter((f) => f.in_stock)
  }, [shroud, config])

  const [selectedId, setSelectedId] = useState<string | null>(null)
  const selected =
    fans.find((f) => f.variant_id === selectedId) ?? fans[0] ?? null

  const headline = shroud
    ? `Your shroud takes ${shroud.fan_qty} x ${shroud.fan_dia}" fans`
    : "Pick your fan setup"

  return (
    <div className="flex flex-col gap-y-4">
      <div>
        <Heading level="h2" className="text-xl">
          {headline}
        </Heading>
        <Text className="text-sm text-gray-600 mt-1">
          The Champion kit is the easy button — SPAL options below if you want
          maximum pull.
        </Text>
      </div>

      {!shroud && fanConfigs.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {fanConfigs.map((c, i) => (
            <button
              key={`${c.qty}x${c.dia}`}
              type="button"
              onClick={() => {
                setConfigIdx(i)
                setSelectedId(null)
              }}
              aria-pressed={i === configIdx}
              data-testid="wizard-fan-config"
              className={
                i === configIdx
                  ? "rounded-full border border-amber-500 bg-amber-50 px-3 py-1.5 text-sm font-medium text-amber-800"
                  : "rounded-full border border-gray-200 bg-white px-3 py-1.5 text-sm font-medium text-gray-700 hover:border-gray-300"
              }
            >
              {`${c.qty} x ${c.dia}"`}
              {i === 0 ? " — max airflow" : ""}
            </button>
          ))}
        </div>
      )}

      <div className="flex flex-col gap-y-3">
        {fans.map((fan) => {
          const isAddedItem = addedIds.has(fan.variant_id)

          return (
            <ChoiceCard
              key={fan.variant_id}
              selected={selected?.variant_id === fan.variant_id}
              added={isAddedItem}
              onSelect={() => setSelectedId(fan.variant_id)}
              data-testid="wizard-fan-option"
            >
              <ItemRow
                item={fan}
                subtitle={qty > 1 ? `${qty} needed` : null}
                added={isAddedItem}
              />
            </ChoiceCard>
          )
        })}
      </div>

      {selected && !addedIds.has(selected.variant_id) && (
        <AmberButton
          autoFocusOnMount
          isLoading={isAdding}
          onClick={() => onAdd(selected, qty)}
          className="w-full"
          data-testid="wizard-fan-add"
        >
          {qty > 1 ? `Add ${qty} fans` : "Add this fan"}
        </AmberButton>
      )}

      <DeclineLink
        onClick={onDecline}
        disabled={isAdding}
        data-testid="wizard-fan-decline"
      >
        No fans — I only drive downhill
      </DeclineLink>
    </div>
  )
}

function RelayStep({
  standard,
  bluetooth,
  addedIds,
  isAdding,
  onAdd,
  onDecline,
}: {
  standard: ProductBySku | null
  bluetooth: ProductBySku | null
  addedIds: Set<string>
  isAdding: boolean
  onAdd: (relay: ProductBySku) => void
  onDecline: () => void
}) {
  // Standard and bluetooth are mutually exclusive: once one is in the cart,
  // stop offering the other.
  const addedRelay =
    [standard, bluetooth].find((r) => r && addedIds.has(r.variant_id)) ?? null

  const options = (
    addedRelay
      ? [addedRelay]
      : [standard, bluetooth].filter(
          (r): r is ProductBySku => Boolean(r?.in_stock)
        )
  ) as ProductBySku[]

  const [selectedId, setSelectedId] = useState(options[0]?.variant_id ?? null)
  const selected = options.find((r) => r.variant_id === selectedId) ?? null

  return (
    <div className="flex flex-col gap-y-4">
      <div>
        <Heading level="h2" className="text-xl">
          Wire it like you mean it
        </Heading>
        <Text className="text-sm text-gray-600 mt-1">
          Fans pull serious amps — a relay kit protects your wiring and switch.
        </Text>
      </div>

      <div className="flex flex-col gap-y-3">
        {options.map((relay) => {
          const isAddedItem = addedIds.has(relay.variant_id)
          const isBluetooth = relay.variant_id === bluetooth?.variant_id

          return (
            <ChoiceCard
              key={relay.variant_id}
              selected={selected?.variant_id === relay.variant_id}
              added={isAddedItem}
              onSelect={() => setSelectedId(relay.variant_id)}
              data-testid="wizard-relay-option"
            >
              <ItemRow
                item={relay}
                subtitle={
                  isBluetooth
                    ? "Control your fans from your phone and watch temps live"
                    : "Plug-and-play protection for your fan circuit"
                }
                added={isAddedItem}
              />
            </ChoiceCard>
          )
        })}
      </div>

      {selected && !addedIds.has(selected.variant_id) && (
        <AmberButton
          autoFocusOnMount
          isLoading={isAdding}
          onClick={() => onAdd(selected)}
          className="w-full"
          data-testid="wizard-relay-add"
        >
          Add relay kit
        </AmberButton>
      )}

      <DeclineLink
        onClick={onDecline}
        disabled={isAdding}
        data-testid="wizard-relay-decline"
      >
        I&apos;ll wire it to a toggle like it&apos;s 1965
      </DeclineLink>
    </div>
  )
}

function ExtrasStep({
  filters,
  tanks,
  inletDiameter,
  addedIds,
  isAdding,
  onAdd,
  onFinish,
  onDecline,
}: {
  filters: ProductBySku[]
  tanks: ProductBySku[]
  inletDiameter: string | null
  addedIds: Set<string>
  isAdding: boolean
  onAdd: (item: ProductBySku) => Promise<boolean>
  onFinish: () => void
  onDecline: () => void
}) {
  const [selectedFilterId, setSelectedFilterId] = useState(
    filters[0]?.variant_id ?? null
  )
  const [selectedTankId, setSelectedTankId] = useState(
    tanks[0]?.variant_id ?? null
  )
  const [addingId, setAddingId] = useState<string | null>(null)

  const selectedFilter =
    filters.find((f) => f.variant_id === selectedFilterId) ?? null
  const selectedTank = tanks.find((t) => t.variant_id === selectedTankId) ?? null

  const filterAdded = filters.some((f) => addedIds.has(f.variant_id))
  const tankAdded = tanks.some((t) => addedIds.has(t.variant_id))

  const handleAdd = async (item: ProductBySku) => {
    setAddingId(item.variant_id)
    await onAdd(item)
    setAddingId(null)
  }

  return (
    <div className="flex flex-col gap-y-5">
      <div>
        <Heading level="h2" className="text-xl">
          Two finishing touches
        </Heading>
      </div>

      {filters.length > 0 && (
        <div className="flex flex-col gap-y-2">
          <div>
            <Text as="span" className="text-sm font-semibold text-gray-900">
              Inline coolant filter
              {inletDiameter
                ? ` — sized to your radiator's ${inletDiameter}" inlet`
                : ""}
            </Text>
            <Text className="text-sm text-gray-600">
              See your coolant quality at a glance and keep debris out of your
              new radiator.
            </Text>
          </div>
          <div className="grid grid-cols-1 small:grid-cols-3 gap-2">
            {filters.map((filter) => {
              const isAddedItem = addedIds.has(filter.variant_id)

              return (
                <ChoiceCard
                  key={filter.variant_id}
                  selected={selectedFilterId === filter.variant_id}
                  added={isAddedItem}
                  onSelect={() => setSelectedFilterId(filter.variant_id)}
                  data-testid="wizard-filter-option"
                >
                  <span className="flex items-center gap-x-3">
                    <Thumb
                      src={filter.thumbnail}
                      alt={filter.title}
                      className="w-12 h-12"
                    />
                    <span className="flex flex-col min-w-0">
                      <span className="text-sm font-medium text-gray-900">
                        {filterColorway(filter.matched_sku)}
                      </span>
                      {fmtPrice(filter.price, filter.currency_code) && (
                        <span className="text-sm font-semibold text-gray-900">
                          {fmtPrice(filter.price, filter.currency_code)}
                        </span>
                      )}
                      {isAddedItem && <AddedCheck />}
                    </span>
                  </span>
                </ChoiceCard>
              )
            })}
          </div>
          {!filterAdded && selectedFilter && (
            <AmberButton
              size="small"
              isLoading={addingId === selectedFilter.variant_id}
              disabled={isAdding}
              onClick={() => handleAdd(selectedFilter)}
              className="self-start"
              data-testid="wizard-filter-add"
            >
              Add filter
            </AmberButton>
          )}
        </div>
      )}

      {tanks.length > 0 && (
        <div className="flex flex-col gap-y-2">
          <div>
            <Text as="span" className="text-sm font-semibold text-gray-900">
              Overflow tank
            </Text>
            <Text className="text-sm text-gray-600">
              Catch expanding coolant instead of dumping it on the ground.
            </Text>
          </div>
          <div className="grid grid-cols-1 small:grid-cols-2 gap-2">
            {tanks.map((tank) => {
              const isAddedItem = addedIds.has(tank.variant_id)

              return (
                <ChoiceCard
                  key={tank.variant_id}
                  selected={selectedTankId === tank.variant_id}
                  added={isAddedItem}
                  onSelect={() => setSelectedTankId(tank.variant_id)}
                  data-testid="wizard-tank-option"
                >
                  <span className="flex items-center gap-x-3">
                    <Thumb
                      src={tank.thumbnail}
                      alt={tank.title}
                      className="w-12 h-12"
                    />
                    <span className="flex flex-col min-w-0">
                      <span className="text-sm font-medium text-gray-900">
                        {tankSize(tank.matched_sku) ?? tank.title}
                      </span>
                      {fmtPrice(tank.price, tank.currency_code) && (
                        <span className="text-sm font-semibold text-gray-900">
                          {fmtPrice(tank.price, tank.currency_code)}
                        </span>
                      )}
                      {isAddedItem && <AddedCheck />}
                    </span>
                  </span>
                </ChoiceCard>
              )
            })}
          </div>
          {!tankAdded && selectedTank && (
            <AmberButton
              size="small"
              isLoading={addingId === selectedTank.variant_id}
              disabled={isAdding}
              onClick={() => handleAdd(selectedTank)}
              className="self-start"
              data-testid="wizard-tank-add"
            >
              Add tank
            </AmberButton>
          )}
        </div>
      )}

      <div className="flex flex-col gap-y-2">
        <AmberButton
          autoFocusOnMount
          disabled={isAdding}
          onClick={onFinish}
          className="w-full"
          data-testid="wizard-extras-finish"
        >
          Finish
        </AmberButton>
        <DeclineLink
          onClick={onDecline}
          disabled={isAdding}
          data-testid="wizard-extras-decline"
        >
          My coolant prefers mystery
        </DeclineLink>
      </div>
    </div>
  )
}

function SummaryStep({
  added,
  close,
}: {
  added: AddedItem[]
  close: () => void
}) {
  const currencyCode = added.find((i) => i.currency_code)?.currency_code ?? null
  const total = added.reduce(
    (sum, i) => sum + (i.price ?? 0) * i.quantity,
    0
  )

  return (
    <div className="flex flex-col gap-y-4">
      <div>
        <Heading level="h2" className="text-xl">
          Cooling system: complete
        </Heading>
        <Text className="text-sm text-gray-600 mt-1">
          Everything below is in your cart and ships together in one box.
        </Text>
      </div>

      <ul className="flex flex-col divide-y divide-gray-100">
        {added.map((item) => (
          <li
            key={item.variant_id}
            className="flex items-center gap-x-3 py-2.5"
            data-testid="wizard-summary-item"
          >
            <Thumb src={item.thumbnail} alt={item.title} className="w-12 h-12" />
            <span className="flex-1 min-w-0 text-sm text-gray-900 line-clamp-2">
              {item.title}
            </span>
            <span className="shrink-0 text-sm text-gray-500">
              x{item.quantity}
            </span>
            {item.price != null && item.currency_code && (
              <span className="shrink-0 text-sm font-semibold text-gray-900">
                {convertToLocale({
                  amount: item.price * item.quantity,
                  currency_code: item.currency_code,
                })}
              </span>
            )}
          </li>
        ))}
      </ul>

      <div className="flex items-center justify-between border-t border-gray-200 pt-3">
        <Text as="span" className="text-sm font-medium text-gray-600">
          Added this session
        </Text>
        <Text
          as="span"
          className="text-base font-semibold text-gray-900"
          data-testid="wizard-summary-total"
        >
          {currencyCode
            ? convertToLocale({ amount: total, currency_code: currencyCode })
            : total.toFixed(2)}
        </Text>
      </div>

      <FinancingMessage amount={total} className="-mt-2" />

      <div className="flex flex-col gap-y-2">
        <LocalizedClientLink
          href="/cart"
          className="inline-flex items-center justify-center rounded-md bg-amber-500 h-12 px-6 text-base font-semibold text-white transition-colors hover:bg-amber-600 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-500 focus-visible:ring-offset-2"
          data-testid="wizard-view-cart"
        >
          View cart
        </LocalizedClientLink>
        <DeclineLink onClick={close} data-testid="wizard-keep-shopping">
          Keep shopping
        </DeclineLink>
      </div>
    </div>
  )
}
